import { useNavigate } from "@tanstack/react-router";
import { Button, CategoryChip, ResultCelebration, RewardPill } from "@ec2/ui";
import { useT } from "@ec2/i18n/react";
import type { MessageKey } from "@ec2/i18n";
import type { QuestCategory } from "@ec2/contracts";
import { CATEGORY_LABEL_KEY, uiCategory, uiState } from "../lib/quests";
import { useActive, useQuestList } from "../state/hooks";
import { useSession } from "../state/session";
import { flattenQuests } from "../lib/quests";
import { InstallGuide } from "../components/install/InstallGuide";
import { NotifyGuide } from "../components/install/NotifyGuide";
import { questMeta } from "./QuestsScreen";
import { Shell } from "../components/Shell";
import { ScreenHeader, LoadingLine } from "../components/common";

const CTA_KEY: Record<QuestCategory, MessageKey> = {
  QR_CODE: "ec2.quest.detail.cta.qr",
  EXTERNAL_QR: "ec2.quest.detail.cta.external",
  PHOTO: "ec2.quest.detail.cta.photo",
  QUIZ: "ec2.quest.detail.cta.quiz",
  FEEDBACK: "ec2.quest.detail.cta.feedback",
  PWA_INSTALL: "ec2.quest.detail.cta.install",
  PUSH_ENABLE: "ec2.quest.detail.cta.push",
};

/** Quest detail (brief §6 card anatomy): description + the type-specific CTA. */
export function QuestDetailScreen({ questId }: { questId: string }) {
  const t = useT();
  const navigate = useNavigate();
  const { locale } = useSession();
  const { active } = useActive();
  const quests = useQuestList(active?.eventId);
  const quest = quests.data ? flattenQuests(quests.data).find((q) => q.id === questId) : undefined;

  if (!quest) {
    return (
      <Shell active="quests">
        {quests.isLoading ? <LoadingLine /> : <ScreenHeader title={t("ec2.error.notFound")} />}
      </Shell>
    );
  }

  const state = uiState(quest);
  const category = uiCategory(quest.category);
  const completion = quest.completion;

  const startCta = () => {
    switch (quest.category) {
      case "PHOTO":
        return void navigate({ to: "/photo/$questId", params: { questId: quest.id } });
      case "QUIZ":
        return void navigate({ to: "/quiz/$questId", params: { questId: quest.id } });
      case "FEEDBACK":
        return void navigate({ to: "/feedback/$questId", params: { questId: quest.id } });
      default:
        return void navigate({ to: "/scan" });
    }
  };

  return (
    <Shell active="quests">
      <ScreenHeader title={quest.name} />
      <div className="flex flex-col gap-4 px-4" data-testid="ec-quest-detail" data-state={state}>
        <div className="flex items-center gap-3">
          <CategoryChip category={category} label={t(CATEGORY_LABEL_KEY[category])} />
          <p className="text-[13px] font-medium text-fg-secondary">{questMeta(quest, t, locale, active?.event.timezone)}</p>
          <span className="ml-auto">
            <RewardPill amount={quest.bucksReward} label={t("ec2.a11y.bucksReward", { amount: quest.bucksReward })} />
          </span>
        </div>
        <p className="max-w-[65ch] text-base text-fg">{quest.description}</p>

        {state === "processing" && (
          <p role="status" className="text-[13px] font-medium text-accent">
            {t("ec2.quest.processing.caption")}
          </p>
        )}

        {state === "solved" && (
          <ResultCelebration
            status="solved"
            title={t("ec2.celebration.solvedToast")}
            amount={quest.bucksReward}
            amountLabel={t("ec2.celebration.earned", { amount: quest.bucksReward })}
            message={completion?.aiExplanation ?? undefined}
          />
        )}

        {state === "failed" && (
          <ResultCelebration
            status="failed"
            title={t("ec2.celebration.failedTitle")}
            message={completion?.aiExplanation ?? completion?.failureReason ?? t("ec2.photo.failedGeneric")}
            actionLabel={quest.category === "PHOTO" ? t("ec2.celebration.tryAgain") : undefined}
            onAction={
              quest.category === "PHOTO"
                ? () => void navigate({ to: "/photo/$questId", params: { questId: quest.id } })
                : undefined
            }
          />
        )}

        {/* Install / notifications: solved on the device, so the page IS the flow. */}
        {state === "active" && quest.category === "PWA_INSTALL" && active && <InstallGuide eventId={active.eventId} />}
        {state === "active" && quest.category === "PUSH_ENABLE" && active && <NotifyGuide eventId={active.eventId} />}

        {state === "active" && quest.category !== "PWA_INSTALL" && quest.category !== "PUSH_ENABLE" && (
          <Button variant="primary" onClick={startCta} data-testid="ec-quest-cta">
            {t(CTA_KEY[quest.category])}
          </Button>
        )}
      </div>
    </Shell>
  );
}
